import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { LuCircleAlert, LuCircleCheck, LuCopy, LuInfo, LuTriangleAlert, LuX } from "react-icons/lu";
import { cn } from "./cn";

export type ToastTone = "success" | "error" | "warning" | "info";

export interface ToastOptions {
  tone: ToastTone;
  title: string;
  body?: string;
  /** Código de referência copiável (request_id dos 5xx). */
  code?: string;
  action?: { label: string; onClick: () => void };
}

const TONE: Record<ToastTone, { icon: React.ReactNode; bar: string; badge: string }> = {
  success: { icon: <LuCircleCheck size={18} />, bar: "border-l-success", badge: "bg-success-soft text-success" },
  error: { icon: <LuCircleAlert size={18} />, bar: "border-l-danger", badge: "bg-danger-soft text-danger" },
  warning: { icon: <LuTriangleAlert size={18} />, bar: "border-l-warning", badge: "bg-warning-soft text-warning" },
  info: { icon: <LuInfo size={18} />, bar: "border-l-primary", badge: "bg-primary-soft text-primary" },
};

const ToastCard: React.FC<ToastOptions & { id: string | number }> = ({ id, tone, title, body, code, action }) => (
  <div
    role={tone === "error" ? "alert" : "status"}
    className={cn("flex w-full min-w-0 gap-3 rounded-panel border border-l-4 border-line bg-surface p-3.5 shadow-modal", TONE[tone].bar)}
  >
    <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-full", TONE[tone].badge)} aria-hidden>
      {TONE[tone].icon}
    </span>
    {/* Tudo quebra linha: o cartão cresce com o texto, sem largura fixa. */}
    <div className="flex min-w-0 flex-1 flex-col gap-1 break-words">
      <strong className="text-body font-semibold text-fg">{title}</strong>
      {body && <p className="text-caption text-fg-muted">{body}</p>}
      {code && (
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(code)}
          className="mt-1 inline-flex max-w-full items-center gap-1.5 self-start break-all rounded-control border border-line bg-surface-hover px-2 py-1 text-left text-caption text-fg-muted"
          aria-label={`Copiar código ${code}`}
        >
          <LuCopy size={14} aria-hidden /> Código: {code}
        </button>
      )}
      {action && (
        <button
          type="button"
          onClick={() => { action.onClick(); toast.dismiss(id); }}
          className="mt-1.5 self-start rounded-button border-[1.5px] border-primary px-3.5 py-2 text-caption font-semibold text-primary hover:bg-primary-tint"
        >
          {action.label}
        </button>
      )}
    </div>
    <button
      type="button"
      onClick={() => toast.dismiss(id)}
      aria-label="Fechar aviso"
      className="-mr-3 -mt-3 grid h-11 w-11 shrink-0 place-items-center self-start rounded-full text-fg-subtle hover:bg-surface-hover"
    >
      <LuX size={16} aria-hidden />
    </button>
  </div>
);

/** Mostra um aviso no cartão padrão. Erros com código ficam mais tempo, para dar tempo de copiar. */
export function notify(options: ToastOptions) {
  return toast.custom((id) => <ToastCard id={id} {...options} />, {
    duration: options.code || options.action ? 8000 : 4000,
  });
}

// Mesmo corte que o sonner usa para o layout de celular (e o mobileOffset).
const DESKTOP = "(min-width: 600px)";

/** Toaster do app: embaixo no celular (perto do polegar), canto superior direito no desktop. */
export const AppToaster: React.FC = () => {
  // jsdom (testes) não tem matchMedia.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia?.(DESKTOP).matches ?? true);

  useEffect(() => {
    if (!window.matchMedia) return;
    const query = window.matchMedia(DESKTOP);
    const update = () => setIsDesktop(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // No celular sobe acima do menu inferior e da ação fixa da página.
  return (
    <Toaster
      position={isDesktop ? "top-right" : "bottom-center"}
      mobileOffset={{ bottom: 150 }}
      richColors
    />
  );
};
